import { useEffect, useLayoutEffect, useRef } from 'react'
import { ArrowUp, AudioLines, LoaderCircle, Paperclip, ShieldCheck, Square } from 'lucide-react'
import { NIET_BEWAARD } from '../../lib/bestanden'
import { BijlageChip } from './Bestanden'
import type { Bijlage } from './useBijlagen'

/* ===========================================================================
 *  Het invoerveld onderaan
 *
 *  Zoals in elke chat-app: Enter verstuurt, Shift+Enter is een nieuwe
 *  regel, het veld groeit mee tot een regel of acht en scrolt daarna zelf.
 *  Terwijl Cowork met een vraag bezig is, wordt de verzendknop een
 *  stopknop. Typen mag dan wel -- je volgende vraag alvast opschrijven --
 *  maar versturen pas als het antwoord er is: de pc op kantoor doet één
 *  vraag tegelijk per gesprek, en een tweede vraag over een half antwoord
 *  heen maakt het gesprek onleesbaar.
 *
 *  Bestanden: met de paperclip, door ze op het gesprek te slepen (zie
 *  Chat.tsx), of door te plakken. Ze staan als chips boven het veld en
 *  zijn al onderweg naar de server zodra ze er staan. Versturen kan pas als
 *  ze allemaal aankwamen, en niet als er een rood is: dan zou Cowork een
 *  vraag beantwoorden over een bestand dat hij nooit kreeg.
 *
 *  Een / ergens op de pagina zet je in het veld, zoals in het dashboard.
 * =========================================================================== */

const MAX_HOOGTE = 8 * 22 + 20

const PAPERCLIP_UITLEG = 'Bestanden toevoegen: PDF, Excel, Word, PowerPoint, CSV, tekst, foto\'s of ZIP, tot 15 MB. '
  + 'Ze worden niet bewaard: Cowork leest ze alleen voor deze vraag. Voor een vervolgvraag voeg je ze opnieuw toe.'

/**
 * Een geplakte schermafdruk heet in elke browser "image.png". Twee daarvan
 * naast elkaar zijn niet uit elkaar te houden; geef ze een naam met de tijd.
 */
function geplakteNaam(f: File, i: number): File {
  if (f.name && f.name !== 'image.png') return f
  const nu = new Date()
  const tijd = `${String(nu.getHours()).padStart(2, '0')}${String(nu.getMinutes()).padStart(2, '0')}`
  const ext = (f.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg')
  return new File([f], `geplakt-${tijd}${i ? `-${i + 1}` : ''}.${ext}`, { type: f.type })
}

export default function Invoer({
  waarde, onWaarde, onStuur, bezig, onStop, stoppen, bijlagen, onBestanden, onWeg, uploadBezig, uploadFout, magBijlagen = true, onPraten,
}: {
  waarde: string
  onWaarde: (w: string) => void
  onStuur: () => void
  bezig: boolean
  onStop: () => void
  stoppen?: boolean
  bijlagen: Bijlage[]
  onBestanden: (f: File[]) => void
  /** Recht cowork.bijlagen: zonder geen paperclip. */
  magBijlagen?: boolean
  /** Praten met Cowork (0201); zonder: geen knop. */
  onPraten?: () => void
  onWeg: (sleutel: string) => void
  uploadBezig: boolean
  uploadFout: boolean
}) {
  const veld = useRef<HTMLTextAreaElement>(null)
  const kiezer = useRef<HTMLInputElement>(null)

  /* Meegroeien: eerst terug naar één regel, dan zo hoog als de tekst. */
  useLayoutEffect(() => {
    const v = veld.current
    if (!v) return
    v.style.height = 'auto'
    v.style.height = `${Math.min(v.scrollHeight, MAX_HOOGTE)}px`
    v.style.overflowY = v.scrollHeight > MAX_HOOGTE ? 'auto' : 'hidden'
  }, [waarde])

  useEffect(() => {
    const focus = () => veld.current?.focus()
    const toets = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return
      const doel = e.target as HTMLElement | null
      if (doel && (doel.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(doel.tagName))) return
      /* Niet als er een venster of menu openstaat: daar hoort de toets bij. */
      if (document.querySelector('.scherm, .uitklap')) return
      e.preventDefault()
      focus()
    }
    window.addEventListener('keydown', toets)
    window.addEventListener('cowork:invoer', focus)
    return () => {
      window.removeEventListener('keydown', toets)
      window.removeEventListener('cowork:invoer', focus)
    }
  }, [])

  /* Op een telefoon niet meteen het toetsenbord openklappen: dat duwt de
     begroeting en de voorbeelden van het scherm. */
  useEffect(() => {
    if (window.matchMedia('(pointer: fine)').matches) veld.current?.focus()
  }, [])

  const klaarBijlagen = bijlagen.filter((b) => b.bestand).length
  const kan = (!!waarde.trim() || klaarBijlagen > 0) && !bezig && !uploadBezig && !uploadFout
  const waaromNiet = uploadFout ? 'Haal eerst de bijlage met een fout weg'
    : uploadBezig ? 'Even wachten tot de bijlagen zijn geüpload' : 'Versturen (Enter)'

  return (
    <form
      className={`invoer-vak ${bezig ? 'bezig' : ''} ${bijlagen.length ? 'met-bijlagen' : ''}`}
      onSubmit={(e) => { e.preventDefault(); if (kan) onStuur() }}
      onClick={(e) => { if (e.target === e.currentTarget) veld.current?.focus() }}
    >
      {bijlagen.length > 0 && (
        <div className="invoer-bijlagen">
          <div className="bijlage-rij" aria-label="Bijlagen" role="group">
            {bijlagen.map((b) => <BijlageChip key={b.sleutel} b={b} onWeg={() => onWeg(b.sleutel)} />)}
          </div>
          <p className="bijlage-noot"><ShieldCheck size={12} aria-hidden /> {NIET_BEWAARD}</p>
        </div>
      )}

      <div className="invoer-rij">
        <input
          ref={kiezer} type="file" multiple hidden tabIndex={-1} aria-hidden
          onChange={(e) => {
            const f = [...(e.target.files ?? [])]
            if (f.length) onBestanden(f)
            e.target.value = ''
          }}
        />
        {magBijlagen && (
          <button type="button" className="invoer-knop clip" onClick={() => kiezer.current?.click()}
            aria-label="Bestanden toevoegen (worden niet bewaard)" title={PAPERCLIP_UITLEG}>
            <Paperclip size={17} />
          </button>
        )}

        <label htmlFor="cowork-vraag" className="verborgen">Je vraag aan Cowork</label>
        <textarea
          id="cowork-vraag"
          ref={veld}
          className="invoer-veld"
          rows={1}
          value={waarde}
          placeholder={bezig ? 'Cowork is met je vraag bezig…' : klaarBijlagen ? 'Wat wil je weten over de bijlage?' : 'Vraag Cowork iets…'}
          onChange={(e) => onWaarde(e.target.value)}
          onPaste={(e) => {
            /* Een plaatje of bestand plakken voegt het toe; gewone tekst
               (ook uit Excel, dat beide meegeeft) blijft gewoon tekst. */
            const f = [...e.clipboardData.files]
            if (f.length && magBijlagen && !e.clipboardData.getData('text/plain')) {
              e.preventDefault()
              onBestanden(f.map(geplakteNaam))
            }
          }}
          onKeyDown={(e) => {
            /* Tijdens het samenstellen van een teken (bijvoorbeeld met een
               dode toets) is Enter geen versturen. */
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              if (kan) onStuur()
            }
          }}
          aria-describedby="invoer-noot"
          enterKeyHint="send"
        />
        <div className="invoer-knoppen">
          {onPraten && !bezig && !waarde.trim() && (
            <button type="button" className="invoer-knop praten-knop" onClick={onPraten}
              aria-label="Praten met Cowork" title="Praten: stel je vraag hardop, Cowork antwoordt met zijn stem">
              <AudioLines size={18} />
            </button>
          )}
          {bezig ? (
            <button type="button" className="invoer-knop stop" onClick={onStop} disabled={stoppen}
              aria-label="Stoppen" title="Stoppen">
              {stoppen ? <LoaderCircle size={16} className="draai" /> : <Square size={13} fill="currentColor" />}
            </button>
          ) : (
            <button type="submit" className={`invoer-knop ${kan ? 'klaar' : ''}`} disabled={!kan}
              aria-label="Versturen" title={waaromNiet}>
              {uploadBezig ? <LoaderCircle size={17} className="draai" /> : <ArrowUp size={18} strokeWidth={2.4} />}
            </button>
          )}
        </div>
      </div>
    </form>
  )
}
